<template>
	<main class="main pay-page page-inside">
		<div class="main-wrapper">
			<header class="section hero-section pay-page__hero">
				<div class="wrapper">
					<div class="pay-page__heading">
						<button
							type="button"
							class="back-page pay-page__back"
							:aria-label="$t('Назад')"
							@click="goBack"
						>
							<img
								class="back-img"
								src="./../img/back-page.svg"
								alt=""
							/>
						</button>

						<div class="pay-page__heading-content">
							<h1 class="pay-page__title">
								{{ $t('Оплата заказа') }}
							</h1>

							<p class="pay-page__subtitle">
								{{
									$t(
										'Введите платёжные данные, чтобы завершить оформление заказа.',
									)
								}}
							</p>
						</div>
					</div>
				</div>
			</header>

			<section class="section pay-section">
				<div class="wrapper">
					<ValidationObserver
						ref="card_form"
						v-slot="{ invalid, handleSubmit }"
					>
						<form
							class="payment-form"
							@submit.prevent="handleSubmit(onCardSubmit)"
						>
							<div class="payment-card">
								<div class="payment-card__header">
									<div>
										<h2 class="payment-card__title">
											{{ $t('Платёжные данные') }}
										</h2>

										<p class="payment-card__description">
											{{
												$t(
													'Все поля банковской карты обязательны для заполнения.',
												)
											}}
										</p>
									</div>

									<div class="payment-card__security">
										<svg
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											aria-hidden="true"
										>
											<path
												d="M7 10V7C7 4.24 9.24 2 12 2C14.76 2 17 4.24 17 7V10"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
											<path
												d="M6 10H18C19.1 10 20 10.9 20 12V20C20 21.1 19.1 22 18 22H6C4.9 22 4 21.1 4 20V12C4 10.9 4.9 10 6 10Z"
												stroke="currentColor"
												stroke-width="2"
											/>
										</svg>

										<span>{{ $t('Безопасная оплата') }}</span>
									</div>
								</div>

								<div class="payment-form__section">
									<h3 class="payment-form__section-title">
										{{ $t('Контактная информация') }}
									</h3>

									<div class="payment-form__grid">
										<label class="payment-field">
											<span class="payment-field__label">
												{{ $t('Email') }}
											</span>

											<input
												v-model.trim="email"
												type="email"
												class="payment-field__input"
												:placeholder="$t('Введите email')"
												autocomplete="email"
											/>
										</label>

										<label class="payment-field">
											<span class="payment-field__label">
												{{ $t('Никнейм') }}
											</span>

											<input
												v-model.trim="nickname"
												type="text"
												class="payment-field__input"
												:placeholder="$t('Введите никнейм')"
												autocomplete="nickname"
											/>
										</label>

										<label class="payment-field payment-field_full">
											<span class="payment-field__label">
												{{ $t('Телефон') }}
											</span>

											<input
												v-model.trim="phone"
												type="tel"
												class="payment-field__input"
												:placeholder="$t('Введите телефон')"
												autocomplete="tel"
												inputmode="tel"
											/>
										</label>
									</div>
								</div>

								<div class="payment-form__divider"></div>

								<div class="payment-form__section">
									<h3 class="payment-form__section-title">
										{{ $t('Банковская карта') }}
									</h3>

									<div class="payment-form__grid">
										<label class="payment-field payment-field_full">
											<span class="payment-field__label">
												{{ $t('Номер карты') }}
											</span>

											<ValidationProvider
												name="formattedPan"
												v-slot="{ errors }"
											>
												<div
													:class="[
														'payment-field__control',
														{
															'payment-field__control_error':
																errors[0],
														},
													]"
												>
													<input
														type="text"
														class="payment-field__input"
														:placeholder="
															$t('0000 0000 0000 0000')
														"
														:value="formattedPan"
														autocomplete="cc-number"
														inputmode="numeric"
														maxlength="19"
														@input="onCardNumberInput"
													/>

													<svg
														class="payment-field__icon"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
														aria-hidden="true"
													>
														<rect
															x="2"
															y="5"
															width="20"
															height="14"
															rx="2"
															stroke="currentColor"
															stroke-width="2"
														/>
														<path
															d="M2 10H22"
															stroke="currentColor"
															stroke-width="2"
														/>
													</svg>
												</div>

												<span
													v-if="errors[0]"
													class="payment-field__error"
												>
													{{ $t(errors[0]) }}
												</span>
											</ValidationProvider>
										</label>

										<label class="payment-field">
											<span class="payment-field__label">
												{{ $t('Срок действия') }}
											</span>

											<ValidationProvider
												name="expiration_date"
												v-slot="{ errors }"
											>
												<div
													:class="[
														'payment-field__control',
														{
															'payment-field__control_error':
																errors[0],
														},
													]"
												>
													<input
														type="text"
														class="payment-field__input"
														:placeholder="$t('MM/YY')"
														:value="expiration_date"
														autocomplete="cc-exp"
														inputmode="numeric"
														maxlength="5"
														@input="onExpirationInput"
													/>
												</div>

												<span
													v-if="errors[0]"
													class="payment-field__error"
												>
													{{ $t(errors[0]) }}
												</span>
											</ValidationProvider>
										</label>

										<label class="payment-field">
											<span class="payment-field__label">
												{{ $t('CVV') }}
											</span>

											<ValidationProvider
												name="card_cvv"
												v-slot="{ errors }"
											>
												<div
													:class="[
														'payment-field__control',
														{
															'payment-field__control_error':
																errors[0],
														},
													]"
												>
													<input
														type="password"
														class="payment-field__input"
														:placeholder="$t('CVV')"
														:value="card_cvv"
														autocomplete="cc-csc"
														inputmode="numeric"
														maxlength="3"
														@input="onCvvInput"
													/>

													<span
														class="payment-field__hint"
														:title="
															$t(
																'Три цифры на обратной стороне карты',
															)
														"
													>
														?
													</span>
												</div>

												<span
													v-if="errors[0]"
													class="payment-field__error"
												>
													{{ $t(errors[0]) }}
												</span>
											</ValidationProvider>
										</label>

										<label class="payment-field payment-field_full">
											<span class="payment-field__label">
												{{ $t('Имя владельца карты') }}
											</span>

											<ValidationProvider
												name="card_holdername"
												v-slot="{ errors }"
											>
												<div
													:class="[
														'payment-field__control',
														{
															'payment-field__control_error':
																errors[0],
														},
													]"
												>
													<input
														type="text"
														class="payment-field__input"
														:placeholder="
															$t('Имя и фамилия латиницей')
														"
														:value="card_holdername"
														autocomplete="cc-name"
														@input="onCardHolderInput"
													/>
												</div>

												<span
													v-if="errors[0]"
													class="payment-field__error"
												>
													{{ $t(errors[0]) }}
												</span>
											</ValidationProvider>
										</label>
									</div>
								</div>

								<div class="payment-form__footer">
									<div class="payment-form__notice">
										<svg
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											aria-hidden="true"
										>
											<path
												d="M12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22Z"
												stroke="currentColor"
												stroke-width="2"
											/>
											<path
												d="M12 11V17"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
											<path
												d="M12 7H12.01"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
										</svg>

										<span>
											{{
												$t(
													'После оплаты вы будете перенаправлены на страницу подтверждения.',
												)
											}}
										</span>
									</div>
									<div class="button-wrapper">
										<transition name="slide">
											<div
												v-if="paymentErrors.length"
												class="cart-message cart-message_error payment-errors"
											>
												<div
													v-for="(error, index) in paymentErrors"
													:key="index"
												>
													{{ error }}
												</div>
											</div>
										</transition>
										<button
											type="submit"
											class="button shadowed payment-form__submit"
											:disabled="invalid || loaded"
										>
											<span v-if="!loaded">
												{{ $t('ОПЛАТИТЬ') }}
											</span>

											<span
												v-else
												class="payment-loader"
												aria-hidden="true"
											></span>
										</button>
									</div>
								</div>
							</div>
						</form>
					</ValidationObserver>
				</div>
			</section>
		</div>
	</main>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';

export default {
	name: 'Pay',

	components: {
		ValidationObserver,
		ValidationProvider,
	},

	data() {
		return {
			formattedPan: '',
			expiration_date: '',
			card_holdername: '',
			card_cvv: '',

			email: '',
			phone: '',
			nickname: '',

			loaded: false,
			paymentErrors: [],
		};
	},

	mounted() {
		this.getProfile();
	},

	methods: {
		goBack() {
			if (this.$parent && typeof this.$parent.back === 'function') {
				this.$parent.back();
				return;
			}

			this.$router.go(-1);
		},

		getProfile() {
			this.$http
				.get('user/profile')
				.then((response) => {
					const profile = response && response.data ? response.data : {};

					this.email = profile.email || '';
					this.phone = profile.phone || '';
					this.nickname = profile.nickname || '';

					const fullName = [profile.name, profile.surname]
						.filter(Boolean)
						.join(' ');

					this.card_holdername = this.formatCardHolderName(fullName);
				})
				.catch((error) => {
					if (
						error.response &&
						error.response.status === 401 &&
						this.$parent &&
						typeof this.$parent.openSignIn === 'function'
					) {
						this.$parent.openSignIn();
					}
				});
		},

		formatCardCvv(value = '') {
			return String(value).replace(/\D/g, '').slice(0, 3);
		},

		formatCardNumber(value = '') {
			const digits = String(value).replace(/\D/g, '').slice(0, 16);

			return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
		},

		formatExpirationDate(value = '') {
			const digits = String(value).replace(/\D/g, '').slice(0, 4);

			if (!digits) {
				return '';
			}

			let month = digits.slice(0, 2);
			const year = digits.slice(2);

			if (month.length === 1 && Number(month) > 1) {
				month = `0${month}`;
			}

			if (month.length === 2) {
				const monthNumber = Number(month);

				if (monthNumber < 1) {
					month = '01';
				}

				if (monthNumber > 12) {
					month = '12';
				}
			}

			return year ? `${month}/${year}` : month;
		},

		formatCardHolderName(value = '') {
			return String(value)
				.replace(/[^A-Za-z\s'-]/g, '')
				.replace(/\s+/g, ' ')
				.trimStart()
				.slice(0, 60)
				.toUpperCase();
		},

		onCardNumberInput(event) {
			this.formattedPan = this.formatCardNumber(event.target.value);

			this.clearPaymentErrors();
		},

		onExpirationInput(event) {
			this.expiration_date = this.formatExpirationDate(event.target.value);

			this.clearPaymentErrors();
		},

		onCardHolderInput(event) {
			this.card_holdername = this.formatCardHolderName(event.target.value);

			this.clearPaymentErrors();
		},

		onCvvInput(event) {
			this.card_cvv = this.formatCardCvv(event.target.value);

			this.clearPaymentErrors();
		},

		async onCardSubmit() {
			if (this.loaded) {
				return;
			}

			this.clearPaymentErrors();

			const [expiryMonth = '', expiryYear = ''] =
				this.expiration_date.split('/');

			const formData = {
				card_number: String(this.formattedPan || '').replace(/\s+/g, ''),

				expiry_month: String(expiryMonth || ''),

				expiry_year: String(expiryYear || ''),

				cardholder_name: String(this.card_holdername || ''),

				cvv: String(this.card_cvv || ''),

				email: String(this.email || ''),

				phone: String(this.phone || ''),

				nickname: String(this.nickname || ''),
			};

			this.loaded = true;

			try {
				const response = await this.$http.post(
					'orders/checkout/execute',
					formData,
				);

				const responseData = response && response.data ? response.data : {};

				if (responseData.status === 'ERROR') {
					this.setServerErrors(responseData);
					return;
				}

				if (responseData.redirect_url) {
					window.location.assign(responseData.redirect_url);

					return;
				}

				this.setPaymentError(this.$t('Failed to get the payment redirect URL.'));
			} catch (error) {
				const responseData =
					error.response && error.response.data ? error.response.data : {};

				if (responseData.message || responseData.errors) {
					this.setServerErrors(responseData);
					return;
				}

				this.setPaymentError(
					error.message ||
						this.$t('Unable to complete payment. Please try again.'),
				);
			} finally {
				this.loaded = false;
			}
		},

		setServerErrors(responseData = {}) {
			const messages = [];

			if (responseData.message) {
				messages.push(String(responseData.message));
			}

			const errors = responseData.errors || {};

			Object.values(errors).forEach((fieldErrors) => {
				const errorList = Array.isArray(fieldErrors)
					? fieldErrors
					: [fieldErrors];

				errorList.forEach((message) => {
					if (
						message === null ||
						message === undefined ||
						message === ''
					) {
						return;
					}

					messages.push(String(message));
				});
			});

			this.paymentErrors = [...new Set(messages)];
		},

		setPaymentError(message) {
			if (message === null || message === undefined || message === '') {
				return;
			}

			this.paymentErrors = [String(message)];
		},

		clearPaymentErrors() {
			this.paymentErrors = [];
		},
	},
};
</script>

<style scoped lang="scss">
.pay-page {
	min-height: 50vh;
	background: var(--white-woodsmoke);
}

.pay-page__hero {
	padding: 44px 16px 24px;

	.wrapper {
		max-width: 930px;
		padding: 0 16px;
	}
}

.pay-page__heading {
	display: flex;
	align-items: flex-start;
	gap: 16px;
}

.pay-page__back {
	flex-shrink: 0;
	width: 42px;
	height: 42px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;

	background: var(--heavy-metal);
	border: 1px solid var(--outer-space);
	border-radius: 10px;

	transition: background-color 0.25s ease, border-color 0.25s ease,
		transform 0.25s ease;

	@media (any-hover: hover) {
		&:hover {
			transform: scale(1.3);
		}
	}
}

.pay-page__heading-content {
	min-width: 0;
}

.pay-page__title {
	margin: 0;

	font-family: var(--font-2);
	font-size: 34px;
	font-weight: 700;
	line-height: 44px;

	color: var(--gray-nurse);
}

.pay-page__subtitle {
	max-width: 620px;
	margin: 8px 0 0;

	font-family: var(--font-1);
	font-size: 14px;
	font-weight: 400;
	line-height: 22px;

	color: var(--pewter);
}

.pay-section {
	padding: 16px 16px 100px;

	> .wrapper {
		max-width: 930px;
	}
}

.payment-form {
	width: 100%;
}

.payment-card {
	position: relative;
	padding: 28px;

	background: var(--bunker-86);
	border: 1px solid var(--outer-space);
	border-radius: 10px;

	box-shadow: 6px 6px 0 #0c0f13;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 0;
		border-radius: inherit;
		pointer-events: none;

		box-shadow: inset 3px 3px 0 rgba(255, 255, 255, 0.025),
			inset -3px -3px 0 rgba(0, 0, 0, 0.22);
	}

	> * {
		position: relative;
		z-index: 1;
	}
}

.payment-card__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
	padding-bottom: 22px;
	border-bottom: 1px solid var(--outer-space);
}

.payment-card__title {
	margin: 0;

	font-family: var(--font-2);
	font-size: 22px;
	font-weight: 600;
	line-height: 29px;

	color: var(--gray-nurse);
}

.payment-card__description {
	max-width: 540px;
	margin: 7px 0 0;

	font-family: var(--font-1);
	font-size: 13px;
	line-height: 20px;

	color: var(--pewter);
}

.payment-card__security {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 11px;

	font-family: var(--font-1);
	font-size: 11px;
	font-weight: 600;
	line-height: 16px;
	white-space: nowrap;

	color: var(--mountain-meadow);
	background: var(--mountain-meadow-10);
	border: 1px solid var(--mountain-meadow-25);
	border-radius: 999px;

	svg {
		width: 15px;
		height: 15px;
	}
}

.payment-form__section {
	padding-top: 24px;
}

.payment-form__section-title {
	margin: 0 0 16px;

	font-family: var(--font-2);
	font-size: 16px;
	font-weight: 600;
	line-height: 22px;

	color: var(--gray-nurse);
}

.payment-form__grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
}

.payment-field {
	min-width: 0;
	margin: 0;
	padding: 0;
}

.payment-field_full {
	grid-column: 1 / -1;
}

.payment-field__label {
	display: block;
	margin-bottom: 7px;

	font-family: var(--font-1);
	font-size: 12px;
	font-weight: 600;
	line-height: 18px;

	color: var(--pewter);
}

.payment-field__optional {
	font-weight: 400;
	opacity: 0.65;
}

.payment-field__control {
	position: relative;
}

.payment-field__input {
	display: block;
	width: 100%;
	height: 44px;
	margin: 0;
	padding: 10px 42px 10px 13px;

	font-family: var(--font-1);
	font-size: 13px;
	font-weight: 400;
	line-height: 20px;

	color: var(--gray-nurse);
	background: var(--white-woodsmoke);
	border: 1px solid var(--outer-space);
	border-radius: 8px;
	outline: none;
	appearance: none;

	box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.35),
		inset -1px -1px 0 rgba(255, 255, 255, 0.03);

	transition: border-color 0.2s ease, box-shadow 0.2s ease,
		background-color 0.2s ease;

	&::placeholder {
		font-family: var(--font-1);
		font-size: 13px;
		color: var(--pewter);
		opacity: 0.55;
	}

	&:focus {
		background: var(--heavy-metal);
		border-color: var(--31);
		box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.35),
			0 0 0 2px rgba(33, 193, 126, 0.12);
	}

	&:-webkit-autofill,
	&:-webkit-autofill:hover,
	&:-webkit-autofill:focus {
		-webkit-text-fill-color: var(--gray-nurse);
		transition: background-color 9999s ease-in-out 0s;
	}
}

.payment-field:not(.payment-field_full) .payment-field__input {
	padding-right: 13px;
}

.payment-field__icon {
	position: absolute;
	top: 50%;
	right: 13px;
	width: 19px;
	height: 19px;
	transform: translateY(-50%);
	pointer-events: none;
	color: var(--pewter);
}

.payment-field__hint {
	position: absolute;
	top: 50%;
	right: 12px;
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	transform: translateY(-50%);

	font-family: var(--font-1);
	font-size: 11px;
	font-weight: 700;
	line-height: 1;

	color: var(--pewter);
	background: var(--heavy-metal);
	border: 1px solid var(--outer-space);
	border-radius: 50%;
}

.payment-field__control_error .payment-field__input {
	border-color: rgba(255, 71, 102, 0.65);
	box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.35),
		0 0 0 2px rgba(255, 71, 102, 0.08);
}

.payment-field__error {
	display: block;
	margin-top: 7px;

	font-family: var(--font-1);
	font-size: 11px;
	font-weight: 400;
	line-height: 16px;

	color: #ff8b9e;
}

.payment-form__divider {
	height: 1px;
	margin-top: 26px;
	background: var(--outer-space);
}

.payment-form__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	margin-top: 28px;
	padding-top: 24px;
	border-top: 1px solid var(--outer-space);
}

.payment-form__notice {
	display: flex;
	align-items: flex-start;
	gap: 9px;
	max-width: 500px;

	font-family: var(--font-1);
	font-size: 12px;
	font-weight: 400;
	line-height: 18px;

	color: var(--pewter);

	svg {
		flex: 0 0 17px;
		width: 17px;
		height: 17px;
		margin-top: 1px;
		color: var(--44);
	}
}

.payment-form__submit {
	flex: 0 0 190px;
	min-height: 46px;
	justify-content: center;
	margin: 0;
	padding: 12px 18px;

	font-family: var(--font-3);
	font-size: 10px;
	line-height: 14px;
}

.payment-loader {
	width: 21px;
	height: 21px;
	display: block;

	border: 3px solid rgba(12, 15, 19, 0.35);
	border-top-color: var(--racing-green);
	border-radius: 50%;

	animation: payment-loader-rotation 0.8s linear infinite;
}

@keyframes payment-loader-rotation {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}

@media (max-width: 767.98px) {
	.pay-page__hero {
		padding: 25px 16px 16px;

		.wrapper {
			padding: 0;
		}
	}

	.pay-page__back {
		width: 36px;
		height: 36px;
	}

	.pay-page__title {
		font-size: 25px;
		line-height: 32px;
	}

	.pay-page__subtitle {
		font-size: 13px;
		line-height: 20px;
	}

	.pay-section {
		padding: 8px 16px 70px;
	}

	.payment-card {
		padding: 20px;
		border-radius: 8px;
	}

	.payment-card__header {
		flex-direction: column;
		gap: 14px;
	}

	.payment-form__grid {
		grid-template-columns: 1fr;
	}

	.payment-field_full {
		grid-column: auto;
	}

	.payment-form__footer {
		align-items: stretch;
		flex-direction: column;
	}

	.payment-form__submit {
		flex-basis: auto;
		width: 100%;
	}
}

@media (max-width: 480px) {
	.pay-page__heading {
		gap: 12px;
	}

	.payment-card {
		padding: 16px;
	}

	.payment-card__security {
		width: 100%;
		justify-content: center;
	}

	.payment-form__section {
		padding-top: 20px;
	}
}

.payment-errors {
	margin-bottom: 16px;
}

.slide-enter-active,
.slide-leave-active {
	transition: opacity 0.25s ease, transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}

.cart-message {
	padding: 10px 12px;
	border-radius: 7px;
	font-family: var(--font-1);
	font-size: 12px;
	line-height: 18px;

	&_error {
		color: #ff8b9e;
		background: rgba(255, 71, 102, 0.08);
		border: 1px solid rgba(255, 71, 102, 0.25);
	}
}

.payment-errors > div + div {
	margin-top: 6px;
}
</style>
